import type { CreateMfaToken, TokenScope } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { createApiClient } from '../../apiClient';
import { updateAuthFromVerifyResponse } from '../../auth/updateAuthFromVerifyResponse';
import { emitEvent } from '../../clientEvents';

type AuthenticateTotpMfaDeviceParams = {
  code: string;
  /** @deprecated Use `requestedScopes` instead. */
  createMfaTokenOptions?: CreateMfaToken;
  deviceId?: string;
  requestedScopes?: TokenScope[];
};

/**
 * Authenticates using a TOTP (Time-based One-Time Password) MFA device.
 *
 * This function validates a TOTP code from an authenticator app and returns
 * an MFA token upon successful authentication.
 *
 * @param params.code - The 6-digit TOTP code from the authenticator app.
 * @param [params.deviceId] - The ID of the specific TOTP device to authenticate with.
 * @param [params.createMfaTokenOptions] - @deprecated Use `requestedScopes` instead. Optional configuration for MFA token creation.
 * @param [params.requestedScopes] - Optional scopes to request an elevated access token instead of an MFA token.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the MFA authentication response.
 */
export const authenticateTotpMfaDevice = async (
  { deviceId, code, createMfaTokenOptions, requestedScopes }: AuthenticateTotpMfaDeviceParams,
  client = getDefaultClient()
) => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  try {
    const response = await apiClient.authMfaTotpDevice({
      environmentId: core.environmentId,
      mFAAuthTotpDevicePostRequest: {
        code,
        createMfaToken: createMfaTokenOptions,
        id: deviceId,
        requestedScopes,
      },
    });

    updateAuthFromVerifyResponse({ response }, client);

    emitEvent(
      {
        args: { deviceId, mfaToken: response.mfaToken },
        event: 'mfaCompletionSuccess',
      },
      client
    );

    return response;
  } catch (error) {
    emitEvent(
      {
        args: { deviceId, error },
        event: 'mfaCompletionFailure',
      },
      client
    );
    throw error;
  }
};
